import IconCircleCheck from '@mnd-frontend/icons/components/CircleCheck';
import {
  GREY_200,
  NEUTRAL_WHITE,
  PURPLE_1000,
  SUCCESS_100,
  SUCCESS_500,
} from '@mnd-frontend/ui/shared/tokens';
import { type ChangeEvent, type FocusEvent, type InputHTMLAttributes, useId } from 'react';
import styled, { css } from 'styled-components';
import ClientOnly from '../ClientOnly';

const Wrapper = styled.div<{ $prReport: boolean; $hasError: boolean }>`
  position: relative;

  i {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${SUCCESS_500};
  }

  label {
    font-weight: normal;
    font-size: var(--font-size-x-small);
    line-height: 1.2;
    color: var(--text-color);
    position: absolute;
    top: 0.5rem;
    left: var(--form-field-padding-x);
    margin: 0;
    pointer-events: none;
    opacity: 0;
    transform: translateY(0.5rem);
    transition: all 0.25s 0s var(--easing);

    ${({ $prReport }) =>
      $prReport &&
      css`
        color: ${NEUTRAL_WHITE};
        opacity: 1;
        top: 0;
        transform: translateY(50%);
        font-size: 0.9375rem;
        line-height: 1.5;
      `}
  }

  input {
    height: 56px;
    appearance: none;
    background-color: var(--form-field-bg);
    border: 1px solid var(--form-field-border-color);
    border-radius: 0;
    color: var(--text-color);
    display: inline-block;
    font-family: var(--body-font-family);
    font-size: 0.9375rem;
    line-height: 1.5;
    margin: 0;
    max-width: 100%;
    outline: 0;
    padding: var(--form-field-padding-y) var(--form-field-padding-x);
    text-align: left;
    text-decoration: none;
    text-transform: none;
    width: 100%;

    transition: all 0.25s 0s var(--easing);

    &:focus {
      border-color: var(--form-field-focus-border-color);
      background-color: var(--form-field-focus-bg);
    }

    &:not(:placeholder-shown) {
      padding-bottom: 0.5rem;
      padding-top: calc(var(--form-field-padding-y) * 2 - 0.5rem);

      + label {
        opacity: 0.5;
        transform: translateY(0);
      }
    }

    &:has(~ i) {
      background-color: ${SUCCESS_100};
    }

    ${({ $hasError }) =>
      $hasError &&
      css`
        background-color: var(--form-field-focus-bg);
        border-color: var(--color-red);
        &:focus {
          border-color: var(--color-red);
        }
      `}

    ${({ $prReport }) =>
      $prReport &&
      css`
        height: 44px;
        background-color: transparent;
        border-radius: 0.5rem;
        color: ${NEUTRAL_WHITE};
        &:focus {
          background-color: transparent;
        }
        &:not(:placeholder-shown) {
          padding-bottom: 0;
          padding-top: 0;
          + label {
            background-color: ${PURPLE_1000};
            color: ${GREY_200};
            opacity: 1;
            padding: 0 0.5rem;
            font-size: 0.55rem;
            transform: translateY(-6px);
          }
        }
      `}
  }
`;

const InputFallback = styled.div`
  height: 56px;
  width: 100%;
  background-color: var(--form-field-bg);
`;

const ErrorText = styled.span`
  color: var(--color-black);
  font-size: var(--font-size-x-small);
  text-align: right;
  white-space: pre-line;
`;

type Props = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  prReport?: boolean;
  errors?: string[];
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  valid?: boolean;
};

const Input = ({
  label,
  prReport = false,
  onChange,
  onBlur,
  onFocus,
  errors,
  valid,
  ...rest
}: Props) => {
  const hasErrors = Boolean(errors && errors.length > 0);

  return (
    <Wrapper $prReport={prReport} $hasError={hasErrors}>
      <input
        {...rest}
        id={label}
        placeholder={prReport ? '' : label}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        autoComplete="off"
      />
      <label htmlFor={label}>{label}</label>
      {valid && <IconCircleCheck />}
    </Wrapper>
  );
};
const ClientOnlyInput = ({
  errors,
  label,
  prReport = false,
  onChange,
  onFocus,
  onBlur,
  valid,
  ...rest
}: Props) => {
  const internalId = useId();
  return (
    <ClientOnly fallBack={<InputFallback />}>
      <Input
        label={label}
        prReport={prReport}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        errors={errors}
        valid={valid}
        aria-describedby={valid ? undefined : internalId}
        {...rest}
      />
      {errors && errors.length > 0 && <ErrorText id={internalId}>{errors.join('\n\n')}</ErrorText>}
    </ClientOnly>
  );
};

export default Input;
export { ClientOnlyInput };
